'use client';
import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
  Divider,
  Pagination,
  LinearProgress,
  Checkbox,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import StorageIcon from '@mui/icons-material/Storage';
import JSZip from 'jszip';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import { useCompanyDataList } from '../../stores/companyStore';
import { fetchSpeedMonitor } from '../../stores/reportStore';
import { useNotifier } from '../../components/Notifier';
import DataTable from '../../components/DataTable';
import ExcelJS from 'exceljs';
import PickerField from '../../components/PickerField';
import MasterVehicleAddEdit from '../master-data/vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../master-data/company/MasterCompanyAddEdit';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import { useLanguage } from '../../utils/i18n';
import { timezone } from '../../utils/timeZone';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

const sanitizeFilename = (str) =>
  String(str || '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim();

const getUserNameFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser);
          if (parsed && typeof parsed === 'object') {
            const name = parsed.name || parsed.username || parsed.email;
            if (name) return String(name);
          }
        } catch {}
      }
      const directName = localStorage.getItem('name') || localStorage.getItem('username');
      if (directName) return String(directName);
    }
  } catch (err) {
    console.warn('cannot read localStorage for user', err);
  }
  return 'User';
};

const buildVehicleWorkbook = ({ licensePlate, vehicleRecords, speedTypesList, getEventLabel, companyName, dateRangeStr, userName }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'OSLOG';
  workbook.created = new Date();
  const exportTimeStr = dayjs().format('DD/MM/YYYY HH:mm:ss');

  speedTypesList.forEach((ev) => {
    const evRows = vehicleRecords.filter((r) => (r.speed_type || r.sensor_type) === ev);
    const evLabel = getEventLabel(ev);
    const sheetTitle = evLabel.slice(0, 31);
    const worksheet = workbook.addWorksheet(sheetTitle);

    const isHarsh = ['Harsh Breaking', 'Harsh Acceleration', 'Harsh Cornering'].includes(ev);
    const isOverSpeed = ev === 'Over Speed';
    const isUnderSpeed = ev === 'Under Speed';

    let columns = [{ header: 'License Plate', key: 'license_plate', width: 18 }];

    if (isHarsh) {
      columns.push({ header: 'Date Time', key: 'date_time', width: 22 });
    } else {
      columns.push({ header: 'Start Time', key: 'start_time', width: 22 });
    }

    columns.push({ header: 'Speed (km/h)', key: 'speed', width: 16 });

    if (isOverSpeed) {
      columns.push({ header: 'Max Speed (km/h)', key: 'max_speed', width: 18 });
    }
    if (isUnderSpeed) {
      columns.push({ header: 'Min Speed (km/h)', key: 'min_speed', width: 18 });
    }

    columns.push(
      { header: 'RPM', key: 'rpm', width: 14 },
      { header: 'Location', key: 'location', width: 45 },
      { header: 'Coordinates', key: 'coordinates', width: 25 }
    );

    worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

    // Metadata Header Rows
    const metaRow1 = worksheet.addRow(['Company Name', companyName || '-']);
    metaRow1.getCell(1).font = { bold: true };
    metaRow1.getCell(2).font = { bold: true };

    const metaRow2 = worksheet.addRow(['Event Type', evLabel || ev || '-']);
    metaRow2.getCell(1).font = { bold: true };
    metaRow2.getCell(2).font = { bold: true };

    const metaRow3 = worksheet.addRow(['Total Data', `${evRows.length} Data`]);
    metaRow3.getCell(1).font = { bold: true };
    metaRow3.getCell(2).font = { bold: true };

    const metaRow4 = worksheet.addRow(['Date Range', dateRangeStr || '-']);
    metaRow4.getCell(1).font = { bold: true };

    const metaRow5 = worksheet.addRow(['Export By', `(${exportTimeStr}) ${userName}`]);
    metaRow5.getCell(1).font = { bold: true };

    worksheet.addRow([]); // Blank spacing row (Row 6)

    // Table Header Row (Row 7)
    const headerRow = worksheet.addRow(columns.map((c) => c.header));
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1976D2' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    evRows.forEach((row) => {
      const rowData = {
        license_plate: row?.vehicle?.license_plate || licensePlate || '-'
      };

      if (isHarsh) {
        rowData.date_time = row?.start_time ? dayjs(row.start_time).format('DD/MM/YYYY HH:mm:ss') : '-';
      } else {
        rowData.start_time = row?.start_time ? dayjs(row.start_time).format('DD/MM/YYYY HH:mm:ss') : '-';
      }

      rowData.speed = row?.speed != null ? row.speed : '-';

      if (isOverSpeed) {
        rowData.max_speed = row?.max_speed != null ? row.max_speed : row?.speed != null ? row.speed : '-';
      }
      if (isUnderSpeed) {
        rowData.min_speed = row?.min_speed != null ? row.min_speed : '-';
      }

      rowData.rpm = row?.engine_rpm != null && row?.engine_rpm !== '' ? row.engine_rpm : '-';
      rowData.location = row?.location || row?.address || '-';
      rowData.coordinates = row?.lat && row?.lon ? `${row.lat}, ${row.lon}` : '-';

      worksheet.addRow(rowData);
    });

    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 7 }];
  });

  return workbook;
};

export default function ReportSensorPage() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();

  const SPEED_TYPES = useMemo(
    () => [
      { value: 'Speed Daily', label: t('reports.dailySpeed', 'Daily Speed') },
      { value: 'Harsh Breaking', label: t('reports.harshBraking', 'Harsh Braking') },
      { value: 'Harsh Acceleration', label: t('reports.harshAcceleration', 'Harsh Acceleration') },
      { value: 'Harsh Cornering', label: t('reports.harshCornering', 'Harsh Cornering') },
      { value: 'Over Speed', label: t('reports.overSpeed', 'Over Speed') },
      { value: 'Under Speed', label: t('reports.underSpeed', 'Under Speed') }
    ],
    [t]
  );

  // Filters
  const [selectedSpeedTypes, setSelectedSpeedTypes] = useState([]);

  const isAllSpeedTypesSelected = SPEED_TYPES.length > 0 && selectedSpeedTypes.length === SPEED_TYPES.length;

  const handleSpeedTypeChange = (event) => {
    const { value } = event.target;
    const values = typeof value === 'string' ? value.split(',') : value;
    if (values.includes('all') || values.includes('select-all')) {
      if (selectedSpeedTypes.length === SPEED_TYPES.length) {
        setSelectedSpeedTypes([]);
      } else {
        setSelectedSpeedTypes(SPEED_TYPES.map((e) => e.value));
      }
    } else {
      setSelectedSpeedTypes(values);
    }
  };

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => selectedVehicles.map((v) => String(v.value)), [selectedVehicles]);
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate(null);
        setToDate(null);
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
    } else if (periodType === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from);
      setToDate(to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [generationTimeRange, setGenerationTimeRange] = useState('');

  // generatedVehicleIds freezes the selected vehicles when Generate is clicked
  const [generatedVehicleIds, setGeneratedVehicleIds] = useState([]);

  // Accordion Pagination
  const [accordionPage, setAccordionPage] = useState(1);
  const accordionItemsPerPage = 5;

  // Data State
  const [vehicleDataMap, setVehicleDataMap] = useState({});
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [activeEventTab, setActiveEventTab] = useState({});

  // Master Data
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  // Convert selectedCompanyIds to array format required by useMasterVehicleList
  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  // Fetch vehicles with server-side company filtering
  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptions = useMemo(() => {
    return vehicleList.map((c) => ({
      value: String(c.id ?? c.vehicle_id ?? c.value),
      label: c.raw?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
      companyName:
        c.original?.join?.company_name ||
        c.original?.company_name ||
        c.join?.company_name ||
        c.company_name ||
        c.companyName ||
        t('reportModule.unknownCompany', 'Unknown Company'),
      raw: c.raw ?? c
    }));
  }, [vehicleList, t]);

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('reportModule.errMaxRange31Days', 'Maximum time span of 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('reports.errStartTimeLater', 'Waktu awal tidak boleh melebihi waktu akhir'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleGenerate = async () => {
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('reports.errSelectVehicle', 'Pilih minimal satu kendaraan'), severity: 'warning' });
      return;
    }
    if (selectedSpeedTypes.length === 0) {
      notify({ message: t('reportModule.errSelectSensorType', 'Pilih minimal satu tipe sensor'), severity: 'warning' });
      return;
    }
    if (!validateDateRange(fromDate, toDate)) return;

    setLoading(true);
    setLoadingProgress(0);
    setLoadingText(t('reportModule.checkingData', 'Checking data...'));
    setGenerationTimeRange(`${dayjs(fromDate).format('DD/MM/YYYY HH:mm')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm')}`);

    const initMap = {};
    selectedVehicleIds.forEach((id) => {
      initMap[id] = { data: [], total: 0, loading: true };
    });
    setVehicleDataMap(initMap);

    // Freeze selected vehicles for rendering
    setGeneratedVehicleIds([...selectedVehicleIds]);
    setAccordionPage(1);

    if (selectedVehicleIds.length === 1) {
      setExpandedAccordion(selectedVehicleIds[0]);
    } else {
      setExpandedAccordion(false);
    }

    const localStart = dayjs(fromDate);
    const localEnd = dayjs(toDate);
    const pType = periodType;
    const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

    let apiChunks = [];
    if (isFullDayPeriod) {
      let curr = localStart.startOf('day');
      const end = localEnd.startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        apiChunks.push({
          startLocal: curr.startOf('day'),
          endLocal: curr.endOf('day'),
          dateStr: curr.format('DD/MM/YYYY')
        });
        curr = curr.add(1, 'day');
      }
    } else {
      let curr = localStart.startOf('day');
      const end = localEnd.startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        let chunkStart = curr.startOf('day');
        let chunkEnd = curr.endOf('day');
        if (curr.isSame(localStart, 'day')) chunkStart = localStart;
        if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

        apiChunks.push({
          startLocal: chunkStart,
          endLocal: chunkEnd,
          dateStr: curr.format('DD/MM/YYYY')
        });
        curr = curr.add(1, 'day');
      }
    }

    const totalVehicles = selectedVehicleIds.length;
    const typesToFetch = selectedSpeedTypes;
    const totalSteps = totalVehicles * apiChunks.length * typesToFetch.length;
    let stepCount = 0;

    for (let vIdx = 0; vIdx < totalVehicles; vIdx++) {
      const vId = selectedVehicleIds[vIdx];
      const vInfo =
        selectedVehicles.find((o) => String(o.value) === String(vId)) || vehicleOptions.find((o) => String(o.value) === String(vId));
      const licensePlate = vInfo?.label || String(vId);
      const vehicleCompanyId = vInfo?.raw?.company || vInfo?.original?.company_id || vInfo?.original?.company || '0';

      let vehicleRecords = [];

      for (let cIdx = 0; cIdx < apiChunks.length; cIdx++) {
        const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
        const chunk = apiChunks[cIdx];
        const dateStr = chunk.dateStr;
        const startDateIso = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
        const endDateIso = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

        for (let tIdx = 0; tIdx < typesToFetch.length; tIdx++) {
          const sType = typesToFetch[tIdx];
          const sLabel = SPEED_TYPES.find((s) => s.value === sType)?.label || sType;

          setLoadingText(
            `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${vIdx + 1}/${totalVehicles}) - ${sLabel} - ${dateStr}`
          );

          let startIdx = 0;
          const length = 1000;
          let hasMore = true;

          while (hasMore) {
            try {
              const res = await fetchSpeedMonitor(vehicleCompanyId, vId, startDateIso, endDateIso, sType, startIdx, length, tz);

              const apiPayload = res?.data || {};
              const records = Array.isArray(apiPayload) ? apiPayload : Array.isArray(apiPayload.data) ? apiPayload.data : [];

              const tagged = records.map((r) => ({
                ...r,
                speed_type: r.speed_type || sType,
                vehicle: r.vehicle || { license_plate: licensePlate, name: licensePlate }
              }));

              vehicleRecords = vehicleRecords.concat(tagged);

              const totalRecord =
                res?.recordsTotal ??
                res?.totalRecord ??
                apiPayload?.recordsTotal ??
                apiPayload?.totalRecord ??
                apiPayload?.total ??
                apiPayload?.recordsFiltered ??
                0;

              startIdx += length;

              if (startIdx >= totalRecord || records.length === 0) {
                hasMore = false;
              }
            } catch (err) {
              console.error(`Error fetching speed monitor for vehicle ${vId} type ${sType}`, err);
              hasMore = false;
            }
          }

          stepCount++;
          setLoadingProgress(Math.min(99, Math.round((stepCount / totalSteps) * 100)));
        }
      }

      vehicleRecords.sort((a, b) => new Date(b.start_time || 0).getTime() - new Date(a.start_time || 0).getTime());

      setVehicleDataMap((prev) => ({
        ...prev,
        [vId]: {
          data: vehicleRecords,
          total: vehicleRecords.length,
          loading: false
        }
      }));
    }

    setLoadingProgress(100);
    setLoading(false);
    notify({ message: t('reports.dataFetchedSuccess', 'Data fetched successfully'), severity: 'success' });
  };

  const getEventLabel = (val) => SPEED_TYPES.find((s) => s.value === val)?.label || val;

  const handleExportVehicle = async (vehicleId, licensePlate) => {
    const vData = vehicleDataMap[vehicleId];
    if (!vData || !vData.data || vData.data.length === 0) {
      notify({ message: t('reports.noDataExport', 'Tidak ada data untuk diexport'), severity: 'warning' });
      return;
    }

    const vInfo =
      selectedVehicles.find((o) => String(o.value) === String(vehicleId)) ||
      vehicleOptions.find((o) => String(o.value) === String(vehicleId));
    const compObj = companyOptions.find((c) => String(c.value) === String(selectedCompanyIds[0]));
    const vehicleCompanyName =
      vInfo?.companyName && vInfo.companyName !== t('reportModule.unknownCompany', 'Unknown Company')
        ? vInfo.companyName
        : compObj?.label || 'Company';
    const compName = sanitizeFilename(vehicleCompanyName);
    const cleanPlate = sanitizeFilename(licensePlate);
    const dateStr = dayjs().format('YYYYMMDD');
    const dateRangeStr = `${dayjs(fromDate).format('DD/MM/YYYY HH:mm:ss')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm:ss')}`;
    const userName = getUserNameFromStorage();

    notify({ message: `${t('reportModule.preparingExportFor', 'Preparing data export for')} ${licensePlate}...`, severity: 'info' });

    const workbook = buildVehicleWorkbook({
      licensePlate,
      vehicleRecords: vData.data,
      speedTypesList: selectedSpeedTypes,
      getEventLabel,
      companyName: vehicleCompanyName,
      dateRangeStr,
      userName
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${cleanPlate}_ReportSensorSpeed_${compName}_${dateStr}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

    notify({ message: t('reportModule.exportSuccess', 'Export successful!'), severity: 'success' });
  };

  const handleExportAll = async () => {
    const vIdsWithData = generatedVehicleIds.filter((vId) => vehicleDataMap[vId]?.data?.length > 0);
    if (vIdsWithData.length === 0) {
      notify({ message: t('reportModule.noAllDataExport', 'There is no aggregate data to export'), severity: 'warning' });
      return;
    }

    const compObj = companyOptions.find((c) => String(c.value) === String(selectedCompanyIds[0]));
    const fallbackCompName = compObj?.label || 'Company';
    const compName = sanitizeFilename(fallbackCompName);
    const dateStr = dayjs().format('YYYYMMDD');
    const dateRangeStr = `${dayjs(fromDate).format('DD/MM/YYYY HH:mm:ss')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm:ss')}`;
    const userName = getUserNameFromStorage();

    notify({ message: t('reportModule.preparingExportAll', 'Preparing for the export of complete vehicles...'), severity: 'info' });

    const zip = new JSZip();

    for (const vId of vIdsWithData) {
      const vInfo =
        selectedVehicles.find((o) => String(o.value) === String(vId)) || vehicleOptions.find((o) => String(o.value) === String(vId));
      const licensePlate = vInfo?.label || String(vId);
      const cleanPlate = sanitizeFilename(licensePlate);
      const vehicleCompanyName =
        vInfo?.companyName && vInfo.companyName !== t('reportModule.unknownCompany', 'Unknown Company')
          ? vInfo.companyName
          : fallbackCompName;
      const vehicleCompName = sanitizeFilename(vehicleCompanyName);

      const workbook = buildVehicleWorkbook({
        licensePlate,
        vehicleRecords: vehicleDataMap[vId].data,
        speedTypesList: selectedSpeedTypes,
        getEventLabel,
        companyName: vehicleCompanyName,
        dateRangeStr,
        userName
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const excelFilename = `${cleanPlate}_ReportSensorSpeed_${vehicleCompName}_${dateStr}.xlsx`;
      zip.file(excelFilename, buffer);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    const url = window.URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ReportSensorSpeed_${compName}_${dateStr}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

    notify({ message: t('reportModule.exportAllSuccess', 'Export all successful!'), severity: 'success' });
  };

  const getColumnsForEvent = (eventType) => {
    const isHarsh = ['Harsh Breaking', 'Harsh Acceleration', 'Harsh Cornering'].includes(eventType);
    const isOverSpeed = eventType === 'Over Speed';
    const isUnderSpeed = eventType === 'Under Speed';

    const cols = [
      {
        field: 'license_plate',
        headerName: t('reports.licensePlate', 'License Plate'),
        width: 140,
        valueGetter: (val, params) => params?.vehicle?.license_plate || '-'
      }
    ];

    if (isHarsh) {
      cols.push({
        field: 'start_time',
        headerName: t('reportModule.dateTime', 'Date Time'),
        width: 180,
        valueGetter: (val, params) => (params?.start_time ? dayjs(params.start_time).format('DD/MM/YYYY HH:mm:ss') : '-')
      });
    } else {
      cols.push({
        field: 'start_time',
        headerName: t('achievement.startTime', 'Start Time'),
        width: 180,
        valueGetter: (val, params) => (params?.start_time ? dayjs(params.start_time).format('DD/MM/YYYY HH:mm:ss') : '-')
      });
    }

    cols.push({
      field: 'speed',
      headerName: t('dashboardAsset.speed', 'Speed (km/h)'),
      width: 130,
      valueGetter: (val, params) => (params?.speed != null ? `${params.speed} km/h` : '-')
    });

    if (isOverSpeed) {
      cols.push({
        field: 'max_speed',
        headerName: t('masterJetty.maxSpeed', 'Max Speed (km/h)'),
        width: 160,
        valueGetter: (val, params) =>
          params?.max_speed != null ? `${params.max_speed} km/h` : params?.speed != null ? `${params.speed} km/h` : '-'
      });
    }
    if (isUnderSpeed) {
      cols.push({
        field: 'min_speed',
        headerName: t('masterVehicle.minSpeed', 'Min Speed (km/h)'),
        width: 160,
        valueGetter: (val, params) =>
          params?.min_speed != null ? `${params.min_speed} km/h` : params?.speed != null ? `${params.speed} km/h` : '-'
      });
    }

    cols.push(
      {
        field: 'engine_rpm',
        headerName: t('exportWaypoint.rpm', 'RPM'),
        width: 110,
        valueGetter: (val, params) => (params?.engine_rpm != null && params.engine_rpm !== '' ? `${params.engine_rpm} rpm` : '-')
      },
      {
        field: 'location',
        headerName: t('notification.location', 'Location'),
        flex: 1,
        minWidth: 220,
        valueGetter: (val, params) => params?.location || '-'
      },
      {
        field: 'coordinates',
        headerName: t('exportWaypoint.coordinate', 'Coordinate'),
        width: 160,
        renderCell: (params) => {
          const lat = params.row.lat;
          const lon = params.row.lon;
          if (!lat || !lon) return '-';
          return (
            <a
              href={`http://maps.google.com/maps?z=18&q=${lat},${lon}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0072DA', textDecoration: 'underline', fontWeight: '500' }}
            >
              {Number(lat).toFixed(4)}, {Number(lon).toFixed(4)}
            </a>
          );
        }
      }
    );

    return cols;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
          <StorageIcon fontSize="large" color="primary" />
          {t('exportWaypoint.reportSensorSpeed', 'Report Sensor Speed')}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
        }}
      >
        <Box
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 2
          }}
        >
          {/* <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} /> */}
          <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
            {t(
              'reports.sensorSpeedDesc',
              'Sensor accumulation for Over Speed, Under Speed, Harsh Acceleration, Harsh Braking, and Daily Speed per Vehicle.'
            )}
          </Typography>
          {(localStorage.getItem('rolename') === 'Administrator' ||
            (() => {
              try {
                return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes('"modules_path":"report-catalog"');
              } catch {
                return false;
              }
            })()) && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={() => navigate('/report-catalog?name=report-catalog')}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'primary',
                  color: '#ffffff',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                }}
              >
                {t('reports.reportCatalog', 'Report Catalog')}
              </Button>
            </Box>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
          <Box sx={{ flex: '1 1 200px' }}>
            <PickerField
              label={t('general.company', 'Company')}
              value={selectedCompanyIds}
              {...companyPickerParams}
              options={companyOptions}
              onChange={(ids) => {
                setSelectedCompanyIds(ids || []);
                setSelectedVehicles([]);
              }}
              disabled={loading}
              allowAdd
              AddComponent={MasterCompanyAddEdit}
              dialogTitle={t('general.selectCompany', 'Select Company')}
              multiSelect={true}
            />
          </Box>

          <Box sx={{ flex: '1 1 250px' }}>
            <PickerField
              label={t('reports.selectVehicle', 'Select Vehicle')}
              value={selectedVehicleIds}
              selectedItems={selectedVehicles}
              {...vehiclePickerParams}
              options={vehicleOptions}
              allowAdd
              disabled={loading}
              AddComponent={MasterVehicleAddEdit}
              onChange={(ids) => {
                const newSelected = ids.map((id) => {
                  const strId = String(id);
                  const match = vehicleOptions.find((v) => String(v.value) === strId);
                  if (match) return match;
                  const prevMatch = selectedVehicles.find((v) => String(v.value) === strId);
                  if (prevMatch) return prevMatch;

                  // Extreme fallback: look into raw vehicleList or listOptions
                  const rawMatch =
                    (vehicleList || []).find((v) => String(v.value) === strId) ||
                    (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
                  if (rawMatch) {
                    return {
                      value: strId,
                      label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
                      companyName: rawMatch.companyName,
                      raw: rawMatch.raw
                    };
                  }
                  return { value: strId, label: strId };
                });
                setSelectedVehicles(newSelected);
              }}
              dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
              multiSelect={true}
            />
          </Box>
          <Box sx={{ flex: '1 1 220px' }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="speed-type-label">{t('reports.sensorType', 'Sensor Type')}</InputLabel>
              <MuiSelect
                labelId="speed-type-label"
                multiple
                value={selectedSpeedTypes}
                onChange={handleSpeedTypeChange}
                disabled={loading}
                input={<OutlinedInput label={t('reports.sensorType', 'Sensor Type')} />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>{t('reports.selectSensorType', 'Select Sensor Type')}</em>;
                  }
                  if (selected.length === SPEED_TYPES.length) {
                    return t('reportModule.allEvents', 'All Events');
                  }
                  return selected.map((val) => SPEED_TYPES.find((o) => o.value === val)?.label || val).join(', ');
                }}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={isAllSpeedTypesSelected}
                    indeterminate={selectedSpeedTypes.length > 0 && selectedSpeedTypes.length < SPEED_TYPES.length}
                  />
                  <ListItemText primary={t('reportModule.allEvents', 'All Events')} />
                </MenuItem>
                {SPEED_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={selectedSpeedTypes.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </Box>
          <Box sx={{ flex: '0 1 180px' }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="period-label">{t('revenue.period', 'Period')}</InputLabel>
              <MuiSelect
                labelId="period-label"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                disabled={loading}
                input={<OutlinedInput label={t('revenue.period', 'Period')} />}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                <MenuItem value="week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                <MenuItem value="month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
              </MuiSelect>
            </FormControl>
          </Box>

          {periodType === 'custom' && (
            <Box sx={{ flex: '1 1 350px' }}>
              <Stack direction="row" spacing={2}>
                <DateTimePicker
                  ampm={false}
                  label={t('reports.from', 'From')}
                  timeSteps={{ minutes: 1 }}
                  format="DD/MM/YYYY HH:mm:ss"
                  disabled={loading}
                  value={fromDate}
                  maxDateTime={toDate}
                  onChange={(v) => {
                    const newV = v ? v.second(0) : v;
                    setFromDate(newV);
                    validateDateRange(newV, toDate);
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                />
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                  label={t('reports.to', 'To')}
                  format="DD/MM/YYYY HH:mm:ss"
                  disabled={loading}
                  value={toDate}
                  minDateTime={fromDate}
                  maxDateTime={fromDate ? fromDate.add(31, 'day') : undefined}
                  onChange={(v) => {
                    const newV = v ? v.second(59) : v;
                    setToDate(newV);
                    validateDateRange(fromDate, newV);
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                />
              </Stack>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', mt: { xs: 2, md: 0 } }}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={loading}
              onClick={() => {
                const defaultId = getCompanyIdFromStorage();
                setSelectedCompanyIds(defaultId ? [defaultId] : []);
                setSelectedVehicles([]);
                setSelectedSpeedTypes([]);
                setVehicleDataMap({});
                setGeneratedVehicleIds([]);
                setAccordionPage(1);
                setGenerationTimeRange('');
                setPeriodType('today');
              }}
              sx={{ px: 2, minWidth: 0 }}
            >
              <RestartAltIcon />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || !!dateError || selectedVehicleIds.length === 0 || selectedSpeedTypes.length === 0}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={handleGenerate}
              sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
            >
              {loading ? t('revenue.searching', 'Searching...') : t('general.search', 'Search')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={loading || Object.keys(vehicleDataMap).length === 0}
              startIcon={<FileDownloadIcon />}
              onClick={handleExportAll}
              sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
            >
              {t('reportModule.exportAll', 'Export All')}
            </Button>
          </Box>
        </Box>
        {dateError && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {dateError}
          </Typography>
        )}
      </Paper>
      {generationTimeRange && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 0.5,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(72, 93, 154, 0.05)',
            border: '1px solid rgba(72, 93, 154, 0.2)',
            boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
            {t('revenue.showingDataFrom', 'Showing data from')}{' '}
            <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
              {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
            </Box>{' '}
            {t('revenue.to', 'to')}{' '}
            <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
              {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
            </Box>
          </Typography>
        </Box>
      )}

      {loading && loadingProgress > 0 && (
        <Box
          sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, mb: 2, border: '1px dashed rgba(72, 93, 154, 0.3)', mt: 2 }}
        >
          <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 1.5,
                bgcolor: 'rgba(72, 93, 154, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {loadingProgress}% - {loadingText}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Accordion Results */}
      {generatedVehicleIds.length === 0 && !loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 10,
            px: 3,
            bgcolor: '#ffffff',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'rgba(72, 93, 154, 0.3)',
            boxShadow: '0 4px 24px rgba(72, 93, 154, 0.04)',
            mt: 2
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(72, 93, 154, 0.05)',
              p: 2.5,
              borderRadius: '50%',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <StorageIcon sx={{ fontSize: 56, color: '#b6b6b8', opacity: 0.8 }} />
          </Box>
          <Typography variant="h6" color="#333" fontWeight="700" gutterBottom>
            {t('reportModule.noDataToDisplay', 'No Data to Display')}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth="sm" sx={{ lineHeight: 1.6 }}>
            {t(
              'reportModule.pleaseSelectTheCompanyVehicleAnd',
              'Please select the company, vehicle, and time period from the filter above, then click'
            )}{' '}
            <b>{t('dashboardFuel.generate', 'Generate')}</b>{' '}
            {t('reportModule.toViewTheDetailedSensorReport', 'to view the detailed sensor report.')}
          </Typography>
        </Box>
      )}

      {generatedVehicleIds.length > 0 &&
        generatedVehicleIds.slice((accordionPage - 1) * accordionItemsPerPage, accordionPage * accordionItemsPerPage).map((vId) => {
          const vInfo =
            selectedVehicles.find((o) => String(o.value) === String(vId)) || vehicleOptions.find((o) => String(o.value) === String(vId));
          const vData = vehicleDataMap[vId];

          if (!vData) return null;

          return (
            <Accordion
              key={vId}
              expanded={expandedAccordion === vId}
              onChange={(e, isExpanded) => setExpandedAccordion(isExpanded ? vId : false)}
              sx={{
                mb: 1.5,
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: expandedAccordion === vId ? 'primary.main' : 'divider',
                boxShadow: expandedAccordion === vId ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color={expandedAccordion === vId ? 'primary' : 'action'} />}
                sx={{
                  bgcolor: expandedAccordion === vId ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                  px: 3,
                  minHeight: 64
                }}
              >
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                    <Typography fontWeight="800" variant="h6" color={expandedAccordion === vId ? 'primary.main' : 'text.primary'}>
                      {vInfo?.label || vId}
                    </Typography>
                    <Chip
                      label={vData.loading ? t('general.loading', 'Memuat...') : `${vData.total} ${t('reportModule.records', 'Records')}`}
                      color={vData.total > 0 ? 'primary' : 'default'}
                      variant={expandedAccordion === vId ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportVehicle(vId, vInfo?.label || vId);
                      }}
                      disabled={vData.loading || vData.total === 0}
                      sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'primary.50' } }}
                    >
                      {t('revenue.exportExcel', 'Export Excel')}
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <Divider />
              <AccordionDetails sx={{ p: 0, bgcolor: '#fbfbfb' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#ffffff', px: 2 }}>
                  <Tabs
                    value={activeEventTab[vId] || (selectedSpeedTypes[0] ?? '')}
                    onChange={(e, val) => setActiveEventTab((prev) => ({ ...prev, [vId]: val }))}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: 48,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        minHeight: 48
                      }
                    }}
                  >
                    {selectedSpeedTypes.map((sType) => {
                      const count = (vData.data || []).filter((r) => (r.speed_type || r.sensor_type) === sType).length;
                      const label = getEventLabel(sType);
                      return (
                        <Tab
                          key={sType}
                          value={sType}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <span>{label}</span>
                              <Chip
                                size="small"
                                label={count}
                                color={count > 0 ? 'primary' : 'default'}
                                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 'bold' }}
                              />
                            </Box>
                          }
                        />
                      );
                    })}
                  </Tabs>
                </Box>
                <Box sx={{ p: 2 }}>
                  {(() => {
                    const currentType = activeEventTab[vId] || selectedSpeedTypes[0];
                    const typeRows = (vData.data || []).filter((r) => (r.speed_type || r.sensor_type) === currentType);
                    const typeCols = getColumnsForEvent(currentType);

                    return (
                      <DataTable
                        title={`${vInfo?.label || vId} - ${getEventLabel(currentType)}`}
                        columns={typeCols}
                        rows={typeRows.map((r, i) => ({ ...r, id: r.id || `${vId}_${currentType}_${i}` }))}
                        loading={vData.loading}
                        pagination
                        paginationMode="client"
                        pageSizeOptions={[10, 25, 50, 100]}
                        hideAddButton={true}
                        hideExportButton={true}
                        hideSearchField={false}
                      />
                    );
                  })()}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

      {/* Pagination Controls for Accordions */}
      {generatedVehicleIds.length > accordionItemsPerPage && (
        <Box display="flex" justifyContent="center" mt={3} mb={2}>
          <Pagination
            count={Math.ceil(generatedVehicleIds.length / accordionItemsPerPage)}
            page={accordionPage}
            onChange={(e, val) => {
              setAccordionPage(val);
              setExpandedAccordion(false); // Close expanded accordion on page change
              window.scrollTo({ top: 0, behavior: 'smooth' }); // Optional: scroll up
            }}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </LocalizationProvider>
  );
}
